import { useMemo } from "react";
import vehicleImg from "../assets/images/vehicle_car.png";
import { images } from "../utils/images";
import UserAvatar from "./UserAvatar";
import "../styles/VehicleCard.css";
import { meterIcon } from "../utils/svgs";

const pickValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (!normalized || normalized === "N/A" || normalized === "null") continue;
    return normalized;
  }

  return "";
};

const parseCoordinate = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const formatCoordinate = (value) => Number(value).toFixed(6);

const buildMapBounds = (lat, lng) => {
  const latitudeOffset = 0.008;
  const longitudeOffset = 0.012 / Math.max(Math.cos((lat * Math.PI) / 180), 0.35);

  return {
    west: Number((lng - longitudeOffset).toFixed(6)),
    south: Number((lat - latitudeOffset).toFixed(6)),
    east: Number((lng + longitudeOffset).toFixed(6)),
    north: Number((lat + latitudeOffset).toFixed(6)),
  };
};

const buildCoordinateMapUrl = (lat, lng) => {
  const { west, south, east, north } = buildMapBounds(lat, lng);
  const params = new URLSearchParams({
    bbox: `${west},${south},${east},${north}`,
    layer: "mapnik",
    marker: `${formatCoordinate(lat)},${formatCoordinate(lng)}`,
  });

  return `https://www.openstreetmap.org/export/embed.html?${params.toString()}`;
};

const buildLocationQueryMapUrl = (locationLabel) => {
  const params = new URLSearchParams({
    q: locationLabel,
    z: "15",
    output: "embed",
  });

  return `https://www.google.com/maps?${params.toString()}`;
};

const VehicleCard = ({ row, preview = null, compact = false, showMap = true }) => {
  const resolvedShowMap = preview?.showMap ?? showMap;
  const locationData =
    (preview?.lastLog && typeof preview.lastLog === "object" && preview.lastLog) ||
    (row?.lastLog && typeof row.lastLog === "object" && row.lastLog) ||
    (row?.last_log && typeof row.last_log === "object" && row.last_log) ||
    (row?.current_location && typeof row.current_location === "object" && row.current_location) ||
    null;
  const cardId =
    pickValue(preview?.assetId, row?.vehicleId, row?.equipmentId, row?.tyreId, row?.systemId, row?.id) ||
    "VHC-001";
  const primaryHeading =
    pickValue(
      preview?.primaryText,
      row?.registrationNumber,
      row?.registration_number,
      row?.vehicle_number,
      row?.serialNumber,
      row?.serial_number
    ) || "-";
  const vehicleTitle =
    pickValue(
      preview?.title,
      [row?.make, row?.model].filter(Boolean).join(" "),
      row?.model,
      row?.make,
      row?.description
    ) || "Vehicle Details";
  const vehicleSubtitle =
    pickValue(
      preview?.subtitle,
      [row?.make, row?.model, row?.year].filter(Boolean).join(", "),
      [row?.make, row?.model].filter(Boolean).join(", "),
      row?.description
    ) || "Operational vehicle";
  const statPrimaryText =
    pickValue(preview?.primaryStatText, row?.assignedDriver?.name, row?.driver_name) ||
    "Unassigned Driver";
  const statSecondaryText =
    pickValue(
      preview?.secondaryStatText,
      row?.currentOdometer,
      row?.usageMeterReading,
      row?.totalKmRun,
      row?.odm ? `${row.odm} km` : "",
      row?.odo_meter ? `${row.odo_meter} km` : ""
    ) ||
    "0 km";
  const imageSrc = preview?.imageSrc || vehicleImg;
  const avatarImage = preview?.avatarImage || row?.assignedDriver?.image || "";
  const locationLabel = pickValue(
    preview?.locationLabel,
    locationData?.location,
    row?.lastKnownLocation
  );
  const latitude = parseCoordinate(
    preview?.latitude,
    preview?.lat,
    locationData?.lat,
    locationData?.latitude,
    locationData?.location_latitude,
    row?.lat,
    row?.latitude,
    row?.lastKnownLatitude
  );
  const longitude = parseCoordinate(
    preview?.longitude,
    preview?.lng,
    locationData?.lng,
    locationData?.longitude,
    locationData?.location_longitude,
    row?.lng,
    row?.longitude,
    row?.lastKnownLongitude
  );
  const hasMapCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const hasLocationQuery = Boolean(locationLabel);
  const showLocationBadge = Boolean(locationLabel);
  const mapEmbedUrl = useMemo(() => {
    if (!resolvedShowMap) return "";
    if (hasMapCoordinates) return buildCoordinateMapUrl(latitude, longitude);
    if (hasLocationQuery) return buildLocationQueryMapUrl(locationLabel);
    return "";
  }, [hasLocationQuery, hasMapCoordinates, latitude, longitude, locationLabel, resolvedShowMap]);

  if (compact) {
    return (
      <div className="vehicle_card_wrapper vehicle_card_wrapper_compact">
        <div className="vehicle_card_stats vehicle_card_stats_compact">
          <div className="vehicle_card_stat">
            <div className="vehicle_card_driver_avatar">
              {avatarImage ? (
                <UserAvatar
                  username={statPrimaryText}
                  image={avatarImage}
                  size={32}
                  marginRight="0"
                />
              ) : (
                <img src={images.vehicleDriverImg} alt={statPrimaryText} />
              )}
            </div>
            <span>{statPrimaryText}</span>
          </div>

          <div className="vehicle_card_stat vehicle_card_stat_meter">
            <span className="vehicle_card_meter_icon">{meterIcon}</span>
            <span>{statSecondaryText}</span>
          </div>
        </div>

        <div className="vehicle_card_map vehicle_card_map_compact">
          {mapEmbedUrl ? (
            <iframe
              src={mapEmbedUrl}
              title={locationLabel || `${primaryHeading} location map`}
              className="vehicle_card_map_embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="vehicle_card_map_fallback" />
          )}

          <div className="vehicle_card_map_summary">
            <span className="vehicle_card_map_id">{cardId}</span>
            <strong>{primaryHeading}</strong>
            <p>{vehicleTitle}</p>
          </div>

          {showLocationBadge ? (
            <div className="vehicle_card_location_badge">{locationLabel}</div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`vehicle_card_wrapper${resolvedShowMap ? "" : " vehicle_card_wrapper_no_map"}`}
    >
      <div className="vehicle_card_top">
        <div className="vehicle_card_vehicle_media">
          <img src={imageSrc} alt={primaryHeading} className="vehicle_card_vehicle_image" />
        </div>

        <div className="vehicle_card_content">
          <p className="vehicle_card_id">{cardId}</p>
          <h3>{primaryHeading}</h3>
          {/* <p className="vehicle_card_title">{vehicleTitle}</p> */}
          <p className="vehicle_card_subtitle">{vehicleSubtitle}</p>
        </div>
      </div>

      <div className="vehicle_card_stats">
        <div className="vehicle_card_stat">
          <div className="vehicle_card_driver_avatar">
            {avatarImage ? (
              <UserAvatar
                username={statPrimaryText}
                image={avatarImage}
                size={38}
                marginRight="0"
              />
            ) : (
              <img src={images.vehicleDriverImg} alt={statPrimaryText} />
            )}
          </div>
          <span>{statPrimaryText}</span>
        </div>

        <div className="vehicle_card_stat vehicle_card_stat_meter">
          <span className="vehicle_card_meter_icon">{meterIcon}</span>
          <span>{statSecondaryText}</span>
        </div>
      </div>

      {resolvedShowMap ? (
        <div className="vehicle_card_map">
          {mapEmbedUrl ? (
            <iframe
              src={mapEmbedUrl}
              title={locationLabel || `${primaryHeading} location map`}
              className="vehicle_card_map_embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="vehicle_card_map_fallback" />
          )}

          {showLocationBadge ? (
            <div className="vehicle_card_location_badge">{locationLabel}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default VehicleCard;
